import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import Button from '../../../components/button/Button';
import FilterChip from '../../../components/filter-chip/FilterChip';
import SwitchComponent from '../../../components/form-item/SwitchComponent';
import Toggle from '../../../components/form-item/Toggle';
import HelpHint from '../../../components/help-hint/HelpHint';
import Modal from '../../../components/modal/Modal';
import { isSelectableOptionSlot, shortenOptionChoiceLabels } from './MaOptionSlotChip';
import MaTelopConfigFields from './MaTelopConfigFields';
import { MaTelopConfig, createDefaultMaTelopConfig } from './maTelopConfig';
import { MaSequence } from './maTypes';
import {
  MA_EXPORT_DELIVERABLES,
  MaExportDeliverable,
  MaExportRequest,
  MaExportResult,
  isMaExportRunning,
  planMaExport,
  runMaExport,
} from './runMaExport';
import { MaTimeFormat } from './serializeMaCsv';

/**
 * MA 書き出しモーダル。
 *
 * MA スライドの３点リーダー「書き出し」から開き、
 * - 対象: この動画のみ（表示中セグメント）/ ショット全動画
 * - 成果物（複数選択）: タイムチャート付き動画 / ミックス動画 / ミックス WAV / タイムチャート CSV
 * - オプション候補（複数選択）: 候補ごとに別ファイルを書き出す
 * - 表示設定: テロップのフォント・位置・表示内容（タイムチャート付き動画のみ）
 * を選び、1 件なら単体ダウンロード、複数なら zip でまとめてダウンロードする。
 *
 * レンダリングはブラウザ内（OfflineAudioContext + ffmpeg.wasm）で行い、
 * モーダルを閉じた後もトーストで進捗を表示する。
 */

export interface MaExportModalProps {
  sequence: MaSequence;
  /** 表示中の MA セグメント index（「この動画のみ」の対象）。null なら全動画のみ */
  currentSegmentIndex: number | null;
  /** プレビューで切り替え中のオプション選択（候補の初期選択に使う） */
  previewSelections: Record<string, string>;
  /** プレビューでの切替順（同一位置の動画候補の後勝ち解決に使う） */
  previewSwitchOrder: string[];
  /** 全動画書き出し時のファイル名ベース（MA ファイル名） */
  fileBaseName: string;
  onClose: () => void;
}

type ExportRange = 'current' | 'all';

const DEFAULT_DELIVERABLES: MaExportDeliverable[] = ['telopVideo', 'timeChartCsv'];

/** 書き出し完了のトースト表示（成功件数・失敗理由・音声なしファイルをまとめる） */
function notifyExportResult(result: MaExportResult, toastId: string): void {
  if (result.succeeded === 0) {
    const reason = result.failures[0]?.reason;
    toast.error(reason ? `書き出しに失敗しました（${reason}）` : '書き出しに失敗しました', {
      id: toastId,
      duration: 8000,
    });
    return;
  }
  const notes: string[] = [];
  if (result.failures.length > 0) {
    // 失敗理由は先頭 1 件を添える（同じ原因で複数失敗することが多い）
    notes.push(`${result.failures.length}件は失敗: ${result.failures[0].reason}`);
  }
  if (result.skippedFiles.length > 0) {
    notes.push(`音声なし/取得不可: ${result.skippedFiles.join(', ')}`);
  }
  const message = `${result.succeeded}件を書き出しました${notes.length ? `（${notes.join(' / ')}）` : ''}`;
  if (result.failures.length > 0) {
    toast.error(message, { id: toastId, duration: 12000 });
  } else {
    toast.success(message, { id: toastId, duration: notes.length ? 8000 : 4000 });
  }
}

const MaExportModal = ({
  sequence,
  currentSegmentIndex,
  previewSelections,
  previewSwitchOrder,
  fileBaseName,
  onClose,
}: MaExportModalProps) => {
  const currentSegment =
    currentSegmentIndex != null ? (sequence.segments[currentSegmentIndex] ?? null) : null;

  // 動画のレンダリングは重いので、表示中の動画があればそれを既定にする
  const [range, setRange] = useState<ExportRange>(currentSegment ? 'current' : 'all');
  const [deliverables, setDeliverables] = useState<MaExportDeliverable[]>(DEFAULT_DELIVERABLES);
  const [timeFormat, setTimeFormat] = useState<MaTimeFormat>('frames');
  // 既定で 720p 縮小 ON（wasm シングルスレッドの再エンコード時間を抑える）
  const [scaleTo720p, setScaleTo720p] = useState(true);
  // Stage 1 では保存せず毎回既定値から開始（Stage 2 でプロジェクト設定を初期値にする）
  const [telopConfig, setTelopConfig] = useState<MaTelopConfig>(createDefaultMaTelopConfig);
  const [showTelopSettings, setShowTelopSettings] = useState(false);
  // スロットキー → 選択候補キー。初期値はプレビュー中の選択（未操作は既定候補）
  const [optionChoices, setOptionChoices] = useState<Record<string, string[]>>(() =>
    Object.fromEntries(
      sequence.optionSlots.map(slot => [
        slot.key,
        [previewSelections[slot.key] ?? slot.defaultChoiceKey],
      ]),
    ),
  );

  const segmentIndexes = useMemo(
    () =>
      range === 'current' && currentSegmentIndex != null
        ? [currentSegmentIndex]
        : sequence.segments.map((_, i) => i),
    [range, currentSegmentIndex, sequence.segments],
  );

  // 対象セグメントに属し、候補が 2 つ以上あるスロットだけを出す
  const selectableSlots = useMemo(
    () =>
      sequence.optionSlots.filter(
        slot => segmentIndexes.includes(slot.segmentIndex) && isSelectableOptionSlot(slot),
      ),
    [sequence.optionSlots, segmentIndexes],
  );

  const request: MaExportRequest = useMemo(
    () => ({
      sequence,
      segmentIndexes,
      deliverables,
      optionChoices,
      switchOrder: previewSwitchOrder,
      telopConfig,
      timeFormat,
      scaleTo720p,
      fileBaseName,
    }),
    [
      sequence,
      segmentIndexes,
      deliverables,
      optionChoices,
      previewSwitchOrder,
      telopConfig,
      timeFormat,
      scaleTo720p,
      fileBaseName,
    ],
  );
  const jobCount = useMemo(() => planMaExport(request).length, [request]);

  const wantsTelop = deliverables.includes('telopVideo');
  const wantsCsv = deliverables.includes('timeChartCsv');

  const toggleDeliverable = (kind: MaExportDeliverable) => {
    setDeliverables(prev =>
      prev.includes(kind) ? prev.filter(k => k !== kind) : [...prev, kind],
    );
  };

  const toggleChoice = (slotKey: string, choiceKey: string) => {
    setOptionChoices(prev => {
      const current = prev[slotKey] ?? [];
      if (current.includes(choiceKey)) {
        // 最後の 1 つは外せない（候補なしの組み合わせは作れない）
        if (current.length === 1) return prev;
        return { ...prev, [slotKey]: current.filter(k => k !== choiceKey) };
      }
      return { ...prev, [slotKey]: [...current, choiceKey] };
    });
  };

  const handleExport = () => {
    if (jobCount === 0) return;
    if (isMaExportRunning()) {
      toast.error('別の書き出しが進行中です。完了までお待ちください');
      return;
    }
    // モーダルは閉じ、進捗と結果はトーストで通知する（処理はバックグラウンドで続く）
    const snapshot = request;
    onClose();
    const toastId = toast.loading('書き出しを準備中…');
    void runMaExport(snapshot, p =>
      toast.loading(`${p.current}/${p.total} ${p.message}`, { id: toastId }),
    )
      .then(result => notifyExportResult(result, toastId))
      .catch(e => {
        console.warn('[MA] 書き出しに失敗:', e);
        toast.error(e instanceof Error ? e.message : '書き出しに失敗しました', { id: toastId });
      });
  };

  return (
    <Modal
      title="書き出し"
      description="MAシーケンスのミックス・タイムチャートを書き出します。複数ファイルは zip にまとめます。"
      titleSize="medium"
      size="default"
      onClose={onClose}
      primaryAction={{
        label: jobCount > 1 ? `${jobCount}件を書き出し` : '書き出し',
        onClick: handleExport,
        disabled: jobCount === 0,
      }}
      secondaryAction={{ label: 'キャンセル', onClick: onClose }}
    >
      <div className="u-col u-gap8 u-w100">
        <span className="bodytext-11">対象</span>
        <SwitchComponent<ExportRange>
          value={range}
          options={[
            ...(currentSegment ? [{ value: 'current' as const, label: 'この動画のみ' }] : []),
            { value: 'all', label: 'ショット全動画' },
          ]}
          onChange={v => v && setRange(v)}
        />
      </div>

      <div className="u-col u-gap8 u-w100">
        <div className="u-row u-gap4">
          <span className="bodytext-11">成果物</span>
          <HelpHint
            description={
              'タイムチャート付き動画: クリップの開始位置にテロップを焼き込みます（再エンコード）\n' +
              'ミックス動画: 映像は無劣化コピーし、音声だけミックスに差し替えます\n' +
              'ミックス WAV: 再生と同じミックスを 48kHz / 24bit で書き出します\n' +
              'タイムチャート CSV: 各動画の開始を 0F とした相対時刻の一覧です\n' +
              '動画の書き出しはブラウザ内で処理するため時間がかかります'
            }
          />
        </div>
        <div className="u-row u-gap8 u-wrap">
          {MA_EXPORT_DELIVERABLES.map(d => (
            <FilterChip
              key={d.value}
              multi
              selected={deliverables.includes(d.value)}
              onClick={() => toggleDeliverable(d.value)}
            >
              {d.label}
            </FilterChip>
          ))}
        </div>
      </div>

      {wantsTelop && (
        <div className="u-col u-gap8 u-w100">
          <Toggle value={scaleTo720p} onClick={() => setScaleTo720p(v => !v)}>
            <span className="bodytext-13">720p に縮小して高速化</span>
          </Toggle>
        </div>
      )}

      {wantsCsv && (
        <div className="u-col u-gap8 u-w100">
          <span className="bodytext-11">CSV の時間形式</span>
          <SwitchComponent<MaTimeFormat>
            value={timeFormat}
            options={[
              { value: 'frames', label: '絶対フレーム' },
              { value: 'timecode', label: 'MM:SS.FF' },
            ]}
            onChange={v => v && setTimeFormat(v)}
          />
          <span className="bodytext-11 u-text-light-gray">
            {Math.round(sequence.fps * 100) / 100}fps 基準で換算します
          </span>
        </div>
      )}

      {selectableSlots.length > 0 && (
        <div className="u-col u-gap8 u-w100">
          <div className="u-row u-gap4">
            <span className="bodytext-11">オプション候補</span>
            <HelpHint description="複数選ぶと、候補ごとに別ファイルを書き出します（ファイル名に候補名が付きます）" />
          </div>
          {selectableSlots.map(slot => {
            const labels = shortenOptionChoiceLabels(slot.choices.map(c => c.label));
            const selected = optionChoices[slot.key] ?? [slot.defaultChoiceKey];
            return (
              <div key={slot.key} className="u-col u-gap4 u-w100">
                <span className="bodytext-13">{slot.name}</span>
                <div className="u-row u-gap8 u-wrap">
                  {slot.choices.map((choice, i) => (
                    <FilterChip
                      key={choice.key}
                      multi
                      selected={selected.includes(choice.key)}
                      title={labels[i] !== choice.label ? choice.label : undefined}
                      onClick={() => toggleChoice(slot.key, choice.key)}
                    >
                      {labels[i]}
                    </FilterChip>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {wantsTelop && (
        <div className="u-col u-gap8 u-w100">
          <Button
            size="small"
            theme="tertiary"
            width="fit"
            rightIcon={showTelopSettings ? 'expand_less' : 'expand_more'}
            onClick={() => setShowTelopSettings(v => !v)}
          >
            テロップの表示設定
          </Button>
          {showTelopSettings && (
            <MaTelopConfigFields value={telopConfig} onChange={setTelopConfig} fps={sequence.fps} />
          )}
        </div>
      )}
    </Modal>
  );
};

export default MaExportModal;
